/**
 * Pure (de)serialization helpers for persisting the SSRM grid's column
 * layout — order, visibility, width, and sort — through the consumer-supplied
 * `ICdwSaveComponentStateService` (localStorage-backed in spog-client via
 * `ComponentStateService`).
 *
 * Extracted from `CdwSsrmGridComponent` so the serialization AND the
 * defensive validation of the untrusted persisted blob can be unit-tested
 * without instantiating the component — importing the component class
 * triggers the AG-Grid v28 UMD/ESM interop failure that excludes component
 * specs from vitest (same pure-helper rationale as `selection-lock-revert.ts`
 * and `filter-permissiveness.ts`).
 *
 * Scope is deliberately **order + visibility + width + flex + sort** — NOT
 * pinning. Order/visibility/width/flex persist indefinitely. Sort persists
 * during navigation but resets to the `columnDefs` default on page refresh,
 * login, and logout: `clearPersistedGridSort()` (spog-client) rewrites the
 * blob with the `sort`/`sortIndex` fields dropped, leaving the rest of the
 * layout in place (EDUS-2902 — an earlier version deleted the whole blob and
 * so reset the user's Column Selector choices on every refresh).
 *
 * `flex` is round-tripped alongside `width` because consumers (e.g.
 * device-search) define columns with `flex: 1`. AG-Grid ignores `width` on
 * a flex column; dragging to resize one clears its flex (`flex: null`) and
 * sets a pixel `width`. Persisting `flex` — including `null` — lets restore
 * faithfully reproduce that: resized columns come back fixed-width, while
 * untouched columns keep flexing to fill the viewport.
 */

/** Minimal persisted shape — one entry per column, in display order. */
export interface PersistedColumnState {
  colId: string;
  hide: boolean;
  /** Omitted when AG-Grid reports no numeric width for the column. */
  width?: number;
  /**
   * AG-Grid's flex weight. A number means the column flexes to fill space; an
   * explicit `null` means flex is cleared (the user resized it, so `width`
   * wins). Omitted when AG-Grid reports no flex value for the column.
   */
  flex?: number | null;
  /**
   * Sort direction for this column. 'asc' or 'desc' when sorted, null when
   * not sorted. Omitted when AG-Grid reports no sort for the column.
   */
  sort?: 'asc' | 'desc' | null;
  /**
   * Sort index for multi-column sort. 0 for primary sort column, 1 for
   * secondary, etc. Omitted when column is not sorted.
   */
  sortIndex?: number | null;
}

/**
 * Structural subset of AG-Grid's `ColumnState` we read on save. Declared
 * locally (rather than importing AG-Grid's type) so this helper carries no
 * AG-Grid runtime dependency and stays trivially testable. `colId` is
 * optional here to match AG-Grid's own type — entries without a string
 * `colId` are skipped during serialization.
 */
export interface ColumnStateLike {
  colId?: string | null;
  hide?: boolean | null;
  width?: number | null;
  flex?: number | null;
  sort?: 'asc' | 'desc' | null;
  sortIndex?: number | null;
}

/**
 * Wrapper the persistence service round-trips: `{ columnState: [...] }`,
 * plus the user's chosen `pageSize`. Both ride the same
 * `cdw_component_state_<componentId>` localStorage entry so a single grid
 * has one persisted blob. `pageSize` is optional — a blob saved before this
 * field existed (or by a grid that doesn't expose the page-size selector)
 * simply has no page-size opinion to restore.
 */
export interface PersistedGridState {
  columnState: PersistedColumnState[];
  pageSize?: number;
}

/**
 * Map AG-Grid's `getColumnState()` output to the minimal persisted shape,
 * preserving display order. `hide` defaults to `false` (AG-Grid reports
 * `undefined` for visible columns); `width` is carried only when numeric.
 * `flex` is carried when defined — including an explicit `null`, which is
 * the signal that the user resized a flex column (so `width` must win on
 * restore). Entries without a string `colId` are dropped — they can't be
 * matched back by `applyColumnState` on restore anyway.
 */
export function toPersistedColumnState(
  state: readonly ColumnStateLike[]
): PersistedColumnState[] {
  const result: PersistedColumnState[] = [];
  for (const col of state) {
    if (typeof col.colId !== 'string') continue;
    const persisted: PersistedColumnState = {
      colId: col.colId,
      hide: col.hide ?? false,
    };
    if (typeof col.width === 'number') {
      persisted.width = col.width;
    }
    // Preserve flex faithfully: a number keeps the column flexing; an
    // explicit null keeps it pinned to its width. Only `undefined` is
    // dropped (column has no flex opinion to restore).
    if (col.flex === null || typeof col.flex === 'number') {
      persisted.flex = col.flex;
    }
    // Preserve sort state: 'asc', 'desc', or explicit null when cleared.
    // Only `undefined` is dropped (column has no sort to restore).
    if (col.sort === 'asc' || col.sort === 'desc' || col.sort === null) {
      persisted.sort = col.sort;
    }
    // Preserve sortIndex for multi-column sort. 0 = primary, 1 = secondary, etc.
    if (col.sortIndex === null || typeof col.sortIndex === 'number') {
      persisted.sortIndex = col.sortIndex;
    }
    result.push(persisted);
  }
  return result;
}

/**
 * Type-guard for a value pulled from persistence. localStorage can hold
 * anything (hand-edited, stale schema, corrupted), so anything that isn't a
 * well-formed `PersistedColumnState[]` is rejected — a bad blob degrades to
 * "no restore" rather than throwing inside `applyColumnState`.
 */
export function isPersistedColumnStateArray(
  value: unknown
): value is PersistedColumnState[] {
  return (
    Array.isArray(value) &&
    value.every((item) => {
      if (item === null || typeof item !== 'object') return false;
      const candidate = item as {
        colId?: unknown;
        hide?: unknown;
        width?: unknown;
        flex?: unknown;
        sort?: unknown;
        sortIndex?: unknown;
      };
      return (
        typeof candidate.colId === 'string' &&
        typeof candidate.hide === 'boolean' &&
        (candidate.width === undefined ||
          typeof candidate.width === 'number') &&
        (candidate.flex === undefined ||
          candidate.flex === null ||
          typeof candidate.flex === 'number') &&
        (candidate.sort === undefined ||
          candidate.sort === null ||
          candidate.sort === 'asc' ||
          candidate.sort === 'desc') &&
        (candidate.sortIndex === undefined ||
          candidate.sortIndex === null ||
          typeof candidate.sortIndex === 'number')
      );
    })
  );
}

/**
 * Extract + validate the persisted column layout from whatever
 * `retrieveComponentState` returned. Returns `null` (→ skip restore) for a
 * missing key, a non-object blob, or a malformed `columnState`.
 */
export function readPersistedColumnState(
  saved: unknown
): PersistedColumnState[] | null {
  if (saved === null || typeof saved !== 'object') return null;
  const columnState = (saved as { columnState?: unknown }).columnState;
  return isPersistedColumnStateArray(columnState) ? columnState : null;
}

/**
 * Extract + validate the persisted page size from whatever
 * `retrieveComponentState` returned. Returns `null` (→ keep the default) for
 * a missing key, a non-object blob, or a `pageSize` that isn't a positive
 * finite integer. `allowed`, when supplied, additionally rejects any value
 * not in the selector's option list — a stale blob holding a page size the
 * grid no longer offers degrades to the default rather than to an
 * unselectable size.
 */
export function readPersistedPageSize(
  saved: unknown,
  allowed?: readonly number[]
): number | null {
  if (saved === null || typeof saved !== 'object') return null;
  const pageSize = (saved as { pageSize?: unknown }).pageSize;
  if (
    typeof pageSize !== 'number' ||
    !Number.isInteger(pageSize) ||
    pageSize <= 0
  ) {
    return null;
  }
  if (allowed && !allowed.includes(pageSize)) return null;
  return pageSize;
}

/**
 * SSRM requires each page to be coverable by the block cache. Keeping the
 * block size at least the page size guarantees a page never straddles more
 * blocks than `maxBlocksInCache` can hold (which would blank out rows at
 * large page sizes). Block == page for the 50/100/200/500 options; the 25
 * option keeps the larger default block so small-page paging still fetches
 * two pages per request.
 */
export function resolveCacheBlockSize(
  pageSize: number,
  defaultBlockSize: number
): number {
  return Math.max(pageSize, defaultBlockSize);
}
